import { devError } from '@/lib/logger';
import React, { useContext, useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AuthContext } from '@/App';
import {
  Clock, Users, Calendar, FileText, Settings, LogOut,
  MapPin, Bell, Menu, X, ClipboardList, Camera, BarChart3, Shield
} from 'lucide-react';
import { useCurrentUser, ROLE_LABELS, canViewPhotos } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLayout({ children, title }) {
  const { user, logout } = useContext(AuthContext);
  const { role } = useCurrentUser();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [settings, setSettings] = useState({ test_mode: false });
  const [alerts, setAlerts] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    fetchSettings();
    fetchAlerts();
    fetchPendingCount();
    const id = setInterval(fetchPendingCount, 60_000);
    const onUpdate = () => fetchPendingCount();
    window.addEventListener('requests-updated', onUpdate);
    return () => {
      clearInterval(id);
      window.removeEventListener('requests-updated', onUpdate);
    };
  }, []);

  const fetchSettings = async () => {
    try {
      // Try authenticated settings first, fallback to public
      let res = await fetch(`${API}/settings`, { credentials: 'include' });
      if (!res.ok) {
        res = await fetch(`${API}/settings/public`);
      }
      if (res.ok) {
        const data = await res.json();
        setSettings(data);
      }
    } catch (err) { devError("admin-layout", err);
      // Settings fetch failed
    }
  };

  const fetchAlerts = async () => {
    try {
      const res = await fetch(`${API}/alerts?unread_only=true`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setAlerts(data);
      }
    } catch (err) { devError("admin-layout", err);
      // Alerts fetch failed
    }
  };

  const fetchPendingCount = async () => {
    try {
      const res = await fetch(`${API}/requests/pending-count`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setPendingCount(data.count || 0);
      }
    } catch (err) { devError("admin-layout-pending", err); }
  };

  const allNavItems = [
    { href: '/admin', icon: Clock, label: 'Dashboard' },
    { href: '/admin/kpis', icon: BarChart3, label: 'KPIs' },
    { href: '/admin/adjustments', icon: Shield, label: 'Ajustes' },
    { href: '/admin/employees', icon: Users, label: 'Funcionários' },
    { href: '/admin/schedules', icon: Calendar, label: 'Escalas' },
    { href: '/admin/requests', icon: ClipboardList, label: 'Solicitações', badgeCount: pendingCount },
    { href: '/admin/reports', icon: FileText, label: 'Relatórios' },
    { href: '/admin/photos', icon: Camera, label: 'Fotos', requireAdmin: true },
    { href: '/admin/locations', icon: MapPin, label: 'Locais' },
    { href: '/admin/settings', icon: Settings, label: 'Configurações' },
  ];
  const navItems = allNavItems.filter((item) => {
    if (item.label === 'Fotos' && !canViewPhotos(role)) return false;
    return true;
  });

  const isActive = (href) => location.pathname === href;

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Test Mode Banner */}
      {settings.test_mode && (
        <div className="test-mode-banner h-8 flex items-center justify-center">
          <span className="text-xs font-bold uppercase tracking-widest text-white bg-[#111111] px-3 py-1">
            MODO TESTE ATIVO
          </span>
        </div>
      )}

      {/* Header */}
      <header className="border-b border-[#E5E7EB] bg-white px-4 lg:px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon" 
              className="lg:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
            
            <Link to="/admin" className="flex items-center gap-3">
              <div className="w-8 h-8 bg-[#111111] flex items-center justify-center">
                <Clock className="w-4 h-4 text-white" />
              </div>
              <span className="font-black tracking-tight hidden sm:block" style={{ fontFamily: 'Satoshi, sans-serif' }}>
                TimeTrack Bio
              </span>
            </Link>
          </div>

          <div className="flex items-center gap-4">
            {/* Alerts */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="relative" data-testid="alerts-dropdown">
                  <Bell className="w-5 h-5" />
                  {alerts.length > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#DC2626] text-white text-xs flex items-center justify-center">
                      {alerts.length}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80">
                {alerts.length === 0 ? (
                  <div className="p-4 text-center text-sm text-[#6B7280]">
                    Sem alertas pendentes
                  </div>
                ) : (
                  alerts.slice(0, 5).map(alert => (
                    <DropdownMenuItem key={alert.alert_id} className="flex flex-col items-start p-3">
                      <span className="text-xs uppercase tracking-wider text-[#DC2626] font-semibold">
                        {alert.alert_type === 'late' ? 'Atraso' : 'Falta'}
                      </span>
                      <span className="text-sm">{alert.message}</span>
                      <span className="text-xs text-[#6B7280]">{alert.date}</span>
                    </DropdownMenuItem>
                  ))
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu">
                  {user?.picture ? (
                    <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
                  ) : (
                    <div className="w-8 h-8 bg-[#E5E5E5] rounded-full flex items-center justify-center">
                      <Users className="w-4 h-4" />
                    </div>
                  )}
                  <span className="hidden sm:flex items-center gap-2 text-sm">
                    {user?.name}
                    {role && (
                      <span
                        className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 ${
                          role === 'admin'
                            ? 'bg-[#0029FF] text-white'
                            : role === 'rh'
                            ? 'bg-[#059669] text-white'
                            : 'bg-[#F59E0B] text-white'
                        }`}
                        data-testid="user-role-badge"
                        title={ROLE_LABELS[role]}
                      >
                        {role === 'admin' ? 'ADM' : role === 'rh' ? 'FIN' : 'GEST'}
                      </span>
                    )}
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={logout} data-testid="logout-btn">
                  <LogOut className="w-4 h-4 mr-2" />
                  Sair
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className={`
          fixed lg:static inset-y-0 left-0 z-40 w-64 bg-[#111111] text-white
          transform transition-transform lg:transform-none
          ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          ${settings.test_mode ? 'top-8' : 'top-0'} lg:top-0
        `}>
          <nav className="p-4 pt-20 lg:pt-6 space-y-1">
            {navItems.map(item => (
              <Link
                key={item.href}
                to={item.href}
                data-testid={`nav-${item.label.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/ç/g, 'c')}`}
                onClick={() => setMobileMenuOpen(false)}
                className={`
                  flex items-center gap-3 px-4 py-3 text-sm uppercase tracking-wider
                  transition-colors
                  ${isActive(item.href) 
                    ? 'bg-white text-[#111111]' 
                    : 'hover:bg-white/10'
                  }
                `}
              >
                <item.icon className="w-5 h-5" />
                <span className="flex-1">{item.label}</span>
                {item.badgeCount > 0 && (
                  <span
                    data-testid={`nav-badge-${item.label.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/ç/g, 'c')}`}
                    className={`
                      min-w-[20px] h-5 px-1.5 flex items-center justify-center
                      text-[10px] font-bold rounded-full
                      ${isActive(item.href)
                        ? 'bg-[#DC2626] text-white'
                        : 'bg-[#DC2626] text-white animate-pulse'}
                    `}
                  >
                    {item.badgeCount > 99 ? '99+' : item.badgeCount}
                  </span>
                )}
              </Link>
            ))}
          </nav>
        </aside>

        {/* Overlay */}
        {mobileMenuOpen && (
          <div 
            className="fixed inset-0 bg-black/50 z-30 lg:hidden"
            onClick={() => setMobileMenuOpen(false)}
          />
        )}

        {/* Main Content */}
        <main className="flex-1 p-4 lg:p-6 min-h-[calc(100vh-57px)]">
          {title && (
            <h1 
              className="text-2xl sm:text-3xl font-black tracking-tight mb-6"
              style={{ fontFamily: 'Satoshi, sans-serif' }}
            >
              {title}
            </h1>
          )}
          {children}
        </main>
      </div>
    </div>
  );
}
